"use client"

/**
 * Framer Motion Animation Variants and Utilities
 * Centralized animation configurations for consistent motion design
 */

import type { Variants, Transition } from "framer-motion"

// Easing curves as mutable arrays for framer-motion compatibility
export const easing = {
  ease: [0.25, 0.1, 0.25, 1] as [number, number, number, number],
  easeIn: [0.4, 0, 1, 1] as [number, number, number, number],
  easeOut: [0, 0, 0.2, 1] as [number, number, number, number],
  easeInOut: [0.4, 0, 0.2, 1] as [number, number, number, number],
  spring: { type: "spring" as const, damping: 20, stiffness: 300 },
  springBouncy: { type: "spring" as const, damping: 10, stiffness: 100 },
  springSmooth: { type: "spring" as const, damping: 30, stiffness: 400 },
}

// Standard durations
export const duration = {
  fast: 0.15,
  normal: 0.3,
  slow: 0.5,
  verySlow: 0.8,
} as const

// Page transition variants
export const pageVariants: Variants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: duration.normal,
      ease: easing.easeOut,
    },
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: duration.fast,
      ease: easing.easeIn,
    },
  },
}

// Fade in variants
export const fadeInVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: duration.normal,
      ease: easing.easeOut,
    },
  },
}

// Scale in variants (for modals, popups)
export const scaleInVariants: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: duration.fast,
      ease: easing.easeOut,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: {
      duration: duration.fast,
      ease: easing.easeIn,
    },
  },
}

// Slide in from direction
export const slideVariants = {
  fromLeft: {
    hidden: { x: -50, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: { duration: duration.normal, ease: easing.easeOut },
    },
  },
  fromRight: {
    hidden: { x: 50, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: { duration: duration.normal, ease: easing.easeOut },
    },
  },
  fromTop: {
    hidden: { y: -50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: duration.normal, ease: easing.easeOut },
    },
  },
  fromBottom: {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: duration.normal, ease: easing.easeOut },
    },
  },
}

// Stagger children animation
export const staggerContainerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.05,
    },
  },
}

export const staggerItemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: duration.normal,
      ease: easing.easeOut,
    },
  },
}

// Card hover animation
export const cardHoverVariants: Variants = {
  rest: {
    scale: 1,
    transition: {
      duration: duration.fast,
      ease: easing.easeInOut,
    },
  },
  hover: {
    scale: 1.05,
    y: -8,
    transition: {
      duration: duration.fast,
      ease: easing.easeInOut,
    },
  },
}

// Button tap animation
export const buttonTapVariants: Variants = {
  tap: {
    scale: 0.95,
    transition: {
      duration: 0.1,
      ease: easing.easeInOut,
    },
  },
}

// Float animation (for hero elements)
export const floatVariants: Variants = {
  animate: {
    y: [0, -10, 0],
    transition: {
      duration: 3,
      repeat: Number.POSITIVE_INFINITY,
      ease: easing.easeInOut,
    },
  },
}

// Pulse glow animation
export const pulseGlowVariants: Variants = {
  animate: {
    opacity: [1, 0.8, 1],
    scale: [1, 1.05, 1],
    transition: {
      duration: 2,
      repeat: Number.POSITIVE_INFINITY,
      ease: easing.easeInOut,
    },
  },
}

// Shake animation (for errors/invalid actions)
export const shakeVariants: Variants = {
  shake: {
    x: [0, -10, 10, -10, 10, 0],
    transition: {
      duration: 0.4,
      ease: easing.easeInOut,
    },
  },
}

// Dice roll animation
export const diceRollVariants: Variants = {
  roll: {
    rotateX: [0, 360],
    rotateY: [0, 360],
    scale: [1, 1.2, 1],
    transition: {
      duration: 0.6,
      ease: easing.easeOut,
    },
  },
}

// Token move animation
export const tokenMoveVariants: Variants = {
  move: {
    scale: [1, 1.3, 1],
    y: [0, -15, 0],
    transition: {
      duration: 0.5,
      type: "spring",
      damping: 10,
      stiffness: 100,
    },
  },
}

// Token capture animation
export const tokenCaptureVariants: Variants = {
  capture: {
    scale: [1, 1.5, 0],
    rotate: [0, 360],
    opacity: [1, 0.8, 0],
    transition: {
      duration: 0.6,
      ease: easing.easeOut,
    },
  },
}

// Confetti burst animation
export const confettiBurstVariants: Variants = {
  burst: {
    scale: [0, 1.5, 1],
    opacity: [0, 1, 0.95],
    y: [0, 100],
    transition: {
      duration: 1.5,
      ease: easing.easeOut,
    },
  },
}

// List item animation
export const listItemVariants: Variants = {
  hidden: { x: -20, opacity: 0 },
  visible: (i: number) => ({
    x: 0,
    opacity: 1,
    transition: {
      delay: i * 0.05,
      duration: duration.normal,
      ease: easing.easeOut,
    },
  }),
}

// Utility: Create custom spring transition
export const createSpringTransition = (damping = 20, stiffness = 300, mass = 1): Transition => ({
  type: "spring",
  damping,
  stiffness,
  mass,
})

// Utility: Create custom tween transition
export const createTweenTransition = (
  durationVal: number,
  ease: [number, number, number, number] = easing.easeOut,
  delay = 0,
): Transition => ({
  type: "tween",
  duration: durationVal,
  ease,
  delay,
})

// Reduced motion variants (respects prefers-reduced-motion)
export const getReducedMotionVariants = (variants: Variants): Variants => {
  const reduced: Variants = {}
  for (const key in variants) {
    reduced[key] = {
      ...variants[key],
      transition: { duration: 0.01 },
    }
  }
  return reduced
}
